import React, { useEffect, useState } from 'react';
import {
  Box, Button, Flex, Text, Heading, Card, CardBody, Badge, Spinner, Center,
  HStack, useToast, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
  useDisclosure, useColorModeValue, Icon,
} from '@chakra-ui/react';
import { MdAdd, MdEdit, MdBlock, MdCheckCircle, MdSecurity } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import InputField from 'components/fields/InputField';
import agentesSeguridadApi, { AgenteSeguridad } from 'api/agentesSeguridad';
import { getCurrentUser, isAdmin, esAdminDepartamento } from 'utils/permissions';

const DEPARTAMENTO_SEGURIDAD_ID = 20;

// OJO: a propósito NO se usa el helper tieneModulo() de utils/permissions —
// ese regresa true cuando el usuario no tiene NINGÚN módulo asignado todavía
// (pensado para no restringir sidebar/navegación a alguien sin configurar).
// Aquí se necesita lo contrario: solo entra si el módulo está EXPLÍCITAMENTE
// en su lista, para no abrirle esta pantalla a cualquier usuario normal sin
// configurar.
const tieneModuloExplicito = (clave: string): boolean => {
  const raw = getCurrentUser()?.Modulos;
  if (!raw) return false;
  return String(raw).split(',').map((s: string) => s.trim()).filter(Boolean).includes(clave);
};

// Dos caminos independientes a este permiso: ser admin-tier del departamento
// de Seguridad, O que a un usuario normal (sin ningún rol de admin) se le haya
// asignado explícitamente el módulo "config_agentes_seguridad" desde
// Configuración > Usuarios — ese es justo el caso de un "usuario de
// seguridad" acotado nada más a esta pantalla.
const puedeAdministrar = () => {
  const user = getCurrentUser();
  return (
    isAdmin() ||
    (esAdminDepartamento() && Number(user?.DepartamentoId) === DEPARTAMENTO_SEGURIDAD_ID) ||
    tieneModuloExplicito('config_agentes_seguridad')
  );
};

export default function ConfigAgentesSeguridad() {
  const toast = useToast();
  const navigate = useNavigate();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const sectionBg = useColorModeValue('gray.50', 'navy.700');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [agentes, setAgentes] = useState<AgenteSeguridad[]>([]);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState<AgenteSeguridad | null>(null);
  const [form, setForm] = useState({ Name: '', NIP: '' });

  const usuario = getCurrentUser();

  useEffect(() => {
    if (!puedeAdministrar()) {
      navigate('/admin/dashboards');
    }
  }, [navigate]);

  const cargarAgentes = async () => {
    setLoading(true);
    const data = await agentesSeguridadApi.listado();
    setAgentes(data);
    setLoading(false);
  };

  useEffect(() => { cargarAgentes(); }, []);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ Name: '', NIP: '' });
    onOpen();
  };

  const abrirEditar = (agente: AgenteSeguridad) => {
    setEditando(agente);
    setForm({ Name: agente.Name, NIP: agente.NIP });
    onOpen();
  };

  const mostrarError = (msg: string) =>
    toast({ title: 'Error', description: msg, status: 'error', duration: 5000, isClosable: true });
  const mostrarExito = (msg: string) =>
    toast({ title: 'Éxito', description: msg, status: 'success', duration: 4000, isClosable: true });

  const handleGuardar = async () => {
    if (!form.Name.trim() || !form.NIP.trim()) {
      mostrarError('Completa nombre y NIP');
      return;
    }
    if (!/^\d{4,10}$/.test(form.NIP.trim())) {
      mostrarError('El NIP debe ser numérico, de 4 a 10 dígitos');
      return;
    }
    setGuardando(true);
    try {
      const res = editando
        ? await agentesSeguridadApi.editar(editando.Id, form, usuario.Id)
        : await agentesSeguridadApi.crear(form, usuario.Id);
      if (!res.success) {
        mostrarError(res.error || 'No se pudo guardar');
        return;
      }
      mostrarExito(editando ? 'Agente actualizado' : 'Agente creado');
      onClose();
      cargarAgentes();
    } finally {
      setGuardando(false);
    }
  };

  const handleCambiarEstatus = async (agente: AgenteSeguridad) => {
    const accion = agente.Status === 'active' ? 'desactivar' : 'reactivar';
    if (!window.confirm(`¿Seguro que quieres ${accion} a ${agente.Name}?`)) return;
    const res = agente.Status === 'active'
      ? await agentesSeguridadApi.baja(agente.Id, usuario.Id)
      : await agentesSeguridadApi.reactivar(agente.Id, usuario.Id);
    if (!res.success) {
      mostrarError(res.error || 'No se pudo cambiar el estatus');
      return;
    }
    mostrarExito('Estatus actualizado');
    cargarAgentes();
  };

  if (!puedeAdministrar()) return null;

  return (
    <Box pt={{ base: '130px', md: '80px' }}>
      <Card>
        <CardBody>
          <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
            <Box>
              <Heading size="md" color={textColor} display="flex" alignItems="center" gap={2}>
                <Icon as={MdSecurity} /> Agentes de seguridad
              </Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Cada agente confirma con su propio NIP las salidas, bajas y retornos de activos en portería.
              </Text>
            </Box>
            <Button colorScheme="blue" leftIcon={<Icon as={MdAdd} />} onClick={abrirNuevo}>
              Nuevo agente
            </Button>
          </Flex>

          {loading ? (
            <Center py={10}><Spinner /></Center>
          ) : agentes.length === 0 ? (
            <Text fontSize="sm" color="gray.500">No hay agentes de seguridad registrados todavía.</Text>
          ) : (
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Nombre</Th>
                    <Th>NIP</Th>
                    <Th>Estatus</Th>
                    <Th>Acciones</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {agentes.map((a) => (
                    <Tr key={a.Id}>
                      <Td>{a.Name}</Td>
                      <Td fontFamily="mono">{a.NIP}</Td>
                      <Td>
                        <Badge colorScheme={a.Status === 'active' ? 'green' : 'gray'} variant="subtle">
                          {a.Status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <Button size="xs" variant="ghost" leftIcon={<Icon as={MdEdit} />} onClick={() => abrirEditar(a)}>
                            Editar
                          </Button>
                          <Button
                            size="xs" variant="ghost"
                            colorScheme={a.Status === 'active' ? 'red' : 'green'}
                            leftIcon={<Icon as={a.Status === 'active' ? MdBlock : MdCheckCircle} />}
                            onClick={() => handleCambiarEstatus(a)}
                          >
                            {a.Status === 'active' ? 'Desactivar' : 'Reactivar'}
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editando ? 'Editar agente' : 'Nuevo agente de seguridad'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box bg={sectionBg} border="1px solid" borderColor={borderColor} borderRadius="10px" p={4}>
              <InputField
                id="Name" label="Nombre completo" mb="16px"
                value={form.Name}
                onChange={(e: any) => setForm((p) => ({ ...p, Name: e.target.value }))}
              />
              <InputField
                id="NIP" label="NIP (4 a 10 dígitos)" mb="0px"
                value={form.NIP}
                onChange={(e: any) => setForm((p) => ({ ...p, NIP: e.target.value.replace(/\D/g, '') }))}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={onClose}>Cancelar</Button>
            <Button colorScheme="blue" isLoading={guardando} onClick={handleGuardar}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
